/**
 * B2B Wholesale Order Page — b2b-order.js
 *
 * Features:
 *  - ▲▼ stepper buttons for each size / variant
 *  - Real-time subtotal & grand total calculation
 *  - Category filter
 *  - Sticky cart bar (appears when any qty > 0)
 *  - Bulk add to cart via Shopify Cart API
 */

(function () {
  'use strict';

  /* ── Config ─────────────────────────────────────────────── */
  const config = window.B2B_CONFIG || { discountRate: 30, currency: 'JPY', moneyFormat: '¥{{amount}}' };

  /* ── DOM references ──────────────────────────────────────── */
  const page       = document.querySelector('.b2b-page');
  const cartBar    = document.getElementById('b2b-cart-bar');
  const totalItems = document.getElementById('b2b-total-items');
  const totalPrice = document.getElementById('b2b-total-price');
  const addBtn     = document.getElementById('b2b-add-to-cart');
  const loading    = document.getElementById('b2b-loading');
  const filterBtns = document.querySelectorAll('.b2b-filter__btn');

  if (!page) return;

  const discountRate = parseInt(page.dataset.discount, 10) || config.discountRate;

  /* ── Format money ────────────────────────────────────────── */
  function formatMoney(cents) {
    const amount = Math.round(cents) / 100;
    if (config.currency === 'JPY') {
      return '¥' + Math.round(cents / 100).toLocaleString('ja-JP');
    }
    return config.moneyFormat.replace('{{amount}}', amount.toFixed(2));
  }

  /* ── Stepper helpers ─────────────────────────────────────── */
  function getValue(input) {
    return Math.max(0, parseInt(input.value, 10) || 0);
  }

  function setValue(input, val) {
    input.value = Math.max(0, val);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ── Row subtotal ────────────────────────────────────────── */
  function updateRowSubtotal(row) {
    const price    = parseInt(row.dataset.price, 10) || 0; // in cents
    const inputs   = row.querySelectorAll('.b2b-stepper__input');
    let rowQty     = 0;
    inputs.forEach(function (inp) { rowQty += getValue(inp); });

    const sub = row.querySelector('[data-row-subtotal]');
    if (sub) sub.textContent = formatMoney(price * rowQty);

    row.classList.toggle('has-qty', rowQty > 0);
  }

  /* ── Grand total ─────────────────────────────────────────── */
  function updateGrandTotal() {
    const rows      = document.querySelectorAll('.b2b-row');
    let grandCents  = 0;
    let grandItems  = 0;

    rows.forEach(function (row) {
      const price  = parseInt(row.dataset.price, 10) || 0;
      const inputs = row.querySelectorAll('.b2b-stepper__input');
      inputs.forEach(function (inp) {
        const qty    = getValue(inp);
        grandItems  += qty;
        grandCents  += price * qty;
      });
    });

    totalItems.textContent = grandItems;
    totalPrice.textContent = formatMoney(grandCents);

    const visible = grandItems > 0;
    cartBar.classList.toggle('is-visible', visible);
    cartBar.setAttribute('aria-hidden', String(!visible));
  }

  /* ── Attach stepper events ───────────────────────────────── */
  document.querySelectorAll('.b2b-row').forEach(function (row) {
    row.querySelectorAll('.b2b-size-item__stepper').forEach(function (stepper) {
      const upBtn   = stepper.querySelector('.b2b-stepper__btn--up');
      const downBtn = stepper.querySelector('.b2b-stepper__btn--down');
      const input   = stepper.querySelector('.b2b-stepper__input');
      if (!input) return;

      const stock = parseInt(input.dataset.stock, 10);

      function clamp(v) {
        v = Math.max(0, v);
        if (!isNaN(stock)) v = Math.min(stock, v);
        return v;
      }

      if (upBtn) upBtn.addEventListener('click', function () {
        const next = clamp(getValue(input) + 1);
        setValue(input, next);
        /* 在庫上限に達したら▲を無効化 */
        if (!isNaN(stock)) upBtn.disabled = next >= stock;
        if (downBtn) downBtn.disabled = false;
      });

      if (downBtn) downBtn.addEventListener('click', function () {
        const next = clamp(getValue(input) - 1);
        setValue(input, next);
        if (upBtn && !isNaN(stock)) upBtn.disabled = next >= stock;
        downBtn.disabled = next <= 0;
      });

      input.addEventListener('input', function () {
        const next = clamp(getValue(input));
        input.value = next;
        if (upBtn && !isNaN(stock)) upBtn.disabled = next >= stock;
        if (downBtn) downBtn.disabled = next <= 0;
        updateRowSubtotal(row);
        updateGrandTotal();
      });

      input.addEventListener('change', function () {
        updateRowSubtotal(row);
        updateGrandTotal();
      });
    });
  });

  /* ── Collection filter ───────────────────────────────────── */
  filterBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      filterBtns.forEach(function (b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');

      const col = btn.dataset.collection;
      document.querySelectorAll('.b2b-row').forEach(function (row) {
        row.classList.toggle('is-hidden', col !== 'all' && row.dataset.collection !== col);
      });
    });
  });

  /* ── Infinite scroll ────────────────────────────────────────
     50件ずつ paginate し、テーブル末尾が見えたら次ページを fetch して
     tbody に行を追加する。追加された行にもステッパーイベントを付与。
  ──────────────────────────────────────────────────────────── */
  function attachStepperEvents(rows) {
    rows.forEach(function (row) {
      row.querySelectorAll('.b2b-size-item__stepper').forEach(function (stepper) {
        const upBtn   = stepper.querySelector('.b2b-stepper__btn--up');
        const downBtn = stepper.querySelector('.b2b-stepper__btn--down');
        const input   = stepper.querySelector('.b2b-stepper__input');
        if (!input) return;
        const stock = parseInt(input.dataset.stock, 10);
        function clampV(v) {
          v = Math.max(0, v);
          if (!isNaN(stock)) v = Math.min(stock, v);
          return v;
        }
        if (upBtn) upBtn.addEventListener('click', function () {
          const n = clampV(getValue(input) + 1);
          setValue(input, n);
          if (!isNaN(stock)) upBtn.disabled = n >= stock;
          if (downBtn) downBtn.disabled = false;
        });
        if (downBtn) downBtn.addEventListener('click', function () {
          const n = clampV(getValue(input) - 1);
          setValue(input, n);
          if (upBtn && !isNaN(stock)) upBtn.disabled = n >= stock;
          downBtn.disabled = n <= 0;
        });
        input.addEventListener('input', function () {
          const n = clampV(getValue(input));
          input.value = n;
          if (upBtn && !isNaN(stock)) upBtn.disabled = n >= stock;
          if (downBtn) downBtn.disabled = n <= 0;
          updateRowSubtotal(row);
          updateGrandTotal();
        });
        input.addEventListener('change', function () {
          updateRowSubtotal(row);
          updateGrandTotal();
        });
      });
    });
  }

  (function initInfiniteScroll() {
    const nextPageEl = document.getElementById('b2b-next-page');
    if (!nextPageEl) return;

    const tbody    = document.querySelector('.b2b-table__body');
    const sentinel = document.createElement('tr');
    sentinel.style.height = '1px';
    tbody && tbody.appendChild(sentinel);

    let nextUrl  = nextPageEl.dataset.url;
    let loading  = false;

    async function loadMore() {
      if (!nextUrl || loading) return;
      loading = true;

      try {
        const res  = await fetch(nextUrl);
        const text = await res.text();
        const doc  = new DOMParser().parseFromString(text, 'text/html');
        const rows = Array.from(doc.querySelectorAll('.b2b-row'));

        rows.forEach(function (r) { tbody.insertBefore(r, sentinel); });
        attachStepperEvents(rows);

        const next = doc.getElementById('b2b-next-page');
        nextUrl = next ? next.dataset.url : null;
        if (!nextUrl) observer.disconnect();
      } catch (e) {
        console.error('[B2B] Infinite scroll error:', e);
      } finally {
        loading = false;
      }
    }

    const observer = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: '400px' });

    observer.observe(sentinel);
  })();

  /* ── Add to cart ─────────────────────────────────────────── */
  function buildLineItems() {
    const items = [];
    document.querySelectorAll('.b2b-stepper__input').forEach(function (input) {
      const qty = getValue(input);
      if (qty > 0) {
        items.push({
          id:       parseInt(input.dataset.variantId, 10),
          quantity: qty,
          properties: {
            '_b2b_discount': discountRate + '% wholesale'
          }
        });
      }
    });
    return items;
  }

  async function addToCart(items) {
    const response = await fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ items: items })
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.description || 'Cart error');
    }
    return response.json();
  }

  addBtn && addBtn.addEventListener('click', async function () {
    const items = buildLineItems();
    if (!items.length) return;

    addBtn.disabled = true;
    loading.classList.add('is-visible');
    loading.setAttribute('aria-hidden', 'false');

    try {
      await addToCart(items);

      window.location.href = '/cart';

    } catch (err) {
      console.error('[B2B] Add to cart failed:', err);
      alert('カートへの追加に失敗しました。もう一度お試しください。\n' + err.message);
      addBtn.disabled = false;
      loading.classList.remove('is-visible');
      loading.setAttribute('aria-hidden', 'true');
    }
  });

})();
